import React, { useCallback, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { buildSession, cardState, scheduleReview, toDayKey } from "../lib/learning";
import { progress } from "../lib/progress";
import { hrefFor } from "../lib/router";
import { deckFor, formatWhen, useLearning } from "../lib/useLearning";
import { ProgressBar } from "./Badges";
import CodeWorkspace from "./CodeWorkspace";
import { AnvilIcon, CheckIcon, ChevronRightIcon, XIcon } from "./Icons";
import { Markdown } from "./Markdown";
import QuestionCard from "./QuestionCard";

function Outcome({ outcome, card, track }) {
  const { t, i18n } = useTranslation();
  if (!outcome) return null;
  return (
    <div className={`review-outcome ${outcome.correct ? "is-correct" : "is-incorrect"}`}>
      <p>
        {outcome.correct
          ? t("review.nextIn", { when: formatWhen(outcome.schedule.due, outcome.at, i18n.resolvedLanguage) })
          : t("review.forgot")}
      </p>
      {!outcome.correct && (
        <a href={hrefFor(track.id, card.levelId)} target="_blank" rel="noreferrer">
          {t("review.revisit", { title: card.levelTitle })}
        </a>
      )}
    </div>
  );
}

function ReforgeCard({ card, outcome, onResult, onContinue, isLast, theme }) {
  const { t } = useTranslation();
  const [code, setCode] = useState(card.level.starter);
  // Monaco caches file contents by path; a unique path keeps the learner's old solution out of the editor.
  const editorPath = `reforge-${useId().replace(/:/g, "")}-${card.levelId}.py`;
  const onSolved = useCallback(() => onResult(true), [onResult]);

  return (
    <div className="reforge">
      <section className="reforge__brief">
        <p className="eyebrow">{t("review.reforge")}</p>
        <h2>{t("review.reforgeTitle", { title: card.levelTitle })}</h2>
        <p className="muted small">{t("review.reforgeIntro")}</p>
        <Markdown source={card.level.task} />
      </section>
      <div className="reforge__workspace">
        <CodeWorkspace
          level={card.level}
          code={code}
          onCodeChange={setCode}
          onReset={() => setCode(card.level.starter)}
          onSolved={onSolved}
          onNext={onContinue}
          hasNext={Boolean(outcome)}
          nextLabel={isLast ? t("review.finish") : t("review.continue")}
          theme={theme}
          editorPath={editorPath}
          extraActions={
            !outcome && (
              <button type="button" className="button button--ghost button--small" onClick={() => onResult(false)}>
                {t("review.giveUp")}
              </button>
            )
          }
        />
      </div>
    </div>
  );
}

function Summary({ track, results }) {
  const { t } = useTranslation();
  const { mastery } = useLearning(track);
  const correct = results.filter((result) => result.correct).length;
  const practised = [...new Set(results.flatMap((result) => result.card.concepts))];

  return (
    <div className="review-summary">
      <span className="review-summary__icon" aria-hidden="true">
        <AnvilIcon size={28} />
      </span>
      <h1>{t("review.doneTitle")}</h1>
      <p className="review-summary__score">{t("review.doneScore", { correct, total: results.length })}</p>
      <ul className="review-summary__cards">
        {results.map(({ card, correct: ok }) => (
          <li key={card.id} className={ok ? "is-correct" : "is-incorrect"}>
            {ok ? <CheckIcon size={14} strokeWidth={3} /> : <XIcon size={14} strokeWidth={3} />}
            {card.kind === "reforge" ? t("review.reforgeTitle", { title: card.levelTitle }) : card.levelTitle}
          </li>
        ))}
      </ul>
      <h2 className="section-title">{t("review.doneSkills")}</h2>
      <ul className="skills skills--inline">
        {practised.map((concept) => (
          <li key={concept} className="skill is-learned">
            <span className="skill__name">{concept}</span>
            <span className="skill__pips">
              {[0, 1, 2, 3, 4].map((i) => (
                <span key={i} className={i < (mastery[concept]?.level || 0) ? "is-filled" : ""} />
              ))}
            </span>
          </li>
        ))}
      </ul>
      <a className="button button--primary" href={hrefFor(track.id)}>
        {t("review.back")}
      </a>
    </div>
  );
}

function ReviewPage({ track, theme }) {
  const { t, i18n } = useTranslation();
  const { nextDue, unlocked, now } = useLearning(track);
  // The session is fixed when the page opens, so answering a card does not reshuffle the rest.
  const [session] = useState(() => buildSession(deckFor(track), progress.get(), Date.now()));
  const [index, setIndex] = useState(0);
  const [results, setResults] = useState([]);
  const [outcome, setOutcome] = useState(null);

  const card = session[index];

  const record = useCallback(
    (correct) => {
      if (!card || outcome) return;
      const at = Date.now();
      const schedule = scheduleReview(card, cardState(card, progress.get()), correct, at);
      progress.recordReview(card.id, schedule, toDayKey(at));
      setOutcome({ correct, schedule, at });
      setResults((previous) => [...previous, { card, correct }]);
    },
    [card, outcome],
  );

  const next = () => {
    setOutcome(null);
    setIndex(index + 1);
  };

  if (session.length === 0) {
    return (
      <main id="main" className="page empty-state review-empty">
        <span className="review-summary__icon" aria-hidden="true">
          <AnvilIcon size={28} />
        </span>
        <h1>{t("review.emptyTitle")}</h1>
        <p className="muted">{t("review.emptyBody")}</p>
        <p>
          {unlocked === 0
            ? t("review.emptyNone")
            : t("review.emptyNext", { when: formatWhen(nextDue, now, i18n.resolvedLanguage) })}
        </p>
        <a className="button button--primary" href={hrefFor(track.id)}>
          {t("review.back")}
        </a>
      </main>
    );
  }

  if (!card) {
    return (
      <main id="main" className="page">
        <Summary track={track} results={results} />
      </main>
    );
  }

  const isLast = index === session.length - 1;
  const header = (
    <header className="review-header">
      <div>
        <p className="eyebrow">{t("review.title")}</p>
        <strong>{t("review.progress", { index: index + 1, total: session.length })}</strong>
      </div>
      <ProgressBar value={index + (outcome ? 1 : 0)} total={session.length} label={t("review.title")} />
      <ul className="review-header__concepts">
        {card.concepts.map((concept) => (
          <li key={concept}>{concept}</li>
        ))}
      </ul>
    </header>
  );

  if (card.kind === "reforge") {
    return (
      <main id="main" className="review-page review-page--reforge">
        {header}
        <Outcome outcome={outcome} card={card} track={track} />
        <ReforgeCard
          key={card.id}
          card={card}
          outcome={outcome}
          onResult={record}
          onContinue={next}
          isLast={isLast}
          theme={theme}
        />
        {outcome && !outcome.correct && (
          <div className="review-continue">
            <button type="button" className="button button--primary" onClick={next}>
              {isLast ? t("review.finish") : t("review.continue")}
              <ChevronRightIcon />
            </button>
          </div>
        )}
      </main>
    );
  }

  return (
    <main id="main" className="page review-page">
      {header}
      <QuestionCard
        key={card.id}
        question={card.question}
        eyebrow={t("review.recall")}
        answer={outcome ? results[results.length - 1].answer : undefined}
        onAnswer={(option) => {
          record(option === card.question.answer);
          setResults((previous) => {
            const copy = [...previous];
            copy[copy.length - 1] = { ...copy[copy.length - 1], answer: option };
            return copy;
          });
        }}
      >
        <Outcome outcome={outcome} card={card} track={track} />
      </QuestionCard>
      {outcome && (
        <div className="review-continue">
          <button type="button" className="button button--primary" onClick={next} autoFocus>
            {isLast ? t("review.finish") : t("review.continue")}
            <ChevronRightIcon />
          </button>
        </div>
      )}
    </main>
  );
}

export default ReviewPage;
